import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from "../components/Header";
import { API_BASE_URL, getAssetUrl } from '../api/config';
import { AppContext } from '../contexts/AppContext';
import PlanetsBackground from "../components/PlanetsBackground";
import '../styles/Home.css';

export default function TherapistDashboard() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { dbType } = useContext(AppContext);
    const navigate = useNavigate();

    const [currentUserIndex, setCurrentUserIndex] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');

    const filteredUsers = users.filter(user =>
        String(user.id).toLowerCase().includes(searchQuery.toLowerCase().trim())
    );

    // Reset current user index when search query changes
    useEffect(() => {
        setCurrentUserIndex(0);
    }, [searchQuery]);

    // Keep currentUserIndex clamped to valid range if users count changes
    useEffect(() => {
        if (filteredUsers.length > 0 && currentUserIndex >= filteredUsers.length) {
            setCurrentUserIndex(filteredUsers.length - 1);
        }
    }, [filteredUsers, currentUserIndex]);

    useEffect(() => {
        if (localStorage.getItem('isTherapist') !== 'true') {
            navigate('/therapist/login');
            return;
        }

        fetchUsers();
    }, [dbType]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/therapist/users`, {
                headers: {
                    'x-database-type': dbType
                }
            });
            if (!response.ok) throw new Error('Errore nel caricamento dati');
            const data = await response.json();
            setUsers(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const downloadRecording = async (exerciseId, userId, activityName, level, exerciseNum) => {
        const url = getAssetUrl(`/audio/${exerciseId}`);
        try {
            const response = await fetch(url, {
                headers: {
                    'x-database-type': dbType
                }
            });
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `User${userId}_${activityName}_L${level}_E${exerciseNum}.webm`;
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            alert('Errore nel download della registrazione');
        }
    };

    const deleteRecording = async (exerciseId) => {
        if (!window.confirm('Sei sicuro di voler eliminare questa registrazione? L\'azione è irreversibile.')) {
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/therapist/exercises/${exerciseId}/recording`, {
                method: 'DELETE',
                headers: {
                    'x-database-type': dbType
                }
            });

            if (!response.ok) throw new Error('Errore durante l\'eliminazione');

            // Update UI state
            setUsers(prevUsers => {
                return prevUsers.map(user => ({
                    ...user,
                    activities: user.activities.map(act => ({
                        ...act,
                        levels: act.levels.map(lvl => ({
                            ...lvl,
                            exercises: lvl.exercises.filter(ex => ex.id !== exerciseId)
                        }))
                    }))
                }));
            });

            alert('Registrazione eliminata');
        } catch (err) {
            alert('Errore: ' + err.message);
        }
    };

    const handleDownloadAllLogs = () => {
        window.location.href = `${API_BASE_URL}/therapist/download-zip?dbType=${dbType}`;
    };

    const handleDownloadSelectedUserLogs = () => {
        const currentUser = filteredUsers[currentUserIndex];
        if (!currentUser) return;
        window.location.href = `${API_BASE_URL}/therapist/download-zip?userId=${currentUser.id}&dbType=${dbType}`;
    };

    return (
        <div className="home-wrapper" style={{ overflowY: 'auto', height: '100vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000 }}>
                <Header />
            </div>
            <div className="planet-background" style={{ flex: 1, minHeight: '100vh', padding: '120px 20px 40px' }}>
                <PlanetsBackground />
                <div style={styles.container}>
                    <h1 style={styles.title}>Dashboard Sviluppatore</h1>

                    {/* "Scarica Tutte le Registrazioni" è sempre disponibile da subito:
                        lo ZIP viene generato lato server con i file già salvati, quindi
                        non deve attendere il caricamento della lista utenti. */}
                    <div style={styles.logButtonsContainer}>
                        <button onClick={handleDownloadAllLogs} style={styles.logBtn}>
                            Scarica Tutte le Registrazioni (ZIP)
                        </button>
                    </div>

                    {loading ? (
                        <p style={styles.message}>Caricamento...</p>
                    ) : error ? (
                        <p style={styles.error}>{error}</p>
                    ) : users.length === 0 ? (
                        <p style={styles.message}>Nessun utente trovato con registrazioni.</p>
                    ) : (
                        <div>
                            {/* Barra di ricerca */}
                            <div style={styles.searchContainer}>
                                <input
                                    type="text"
                                    placeholder="🔍 Cerca ID utente..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    style={styles.searchInput}
                                />
                            </div>

                            {/* Download ZIP del solo utente selezionato (richiede la lista caricata) */}
                            <div style={styles.logButtonsContainer}>
                                <button onClick={handleDownloadSelectedUserLogs} style={styles.logBtnSelected}>
                                    Scarica Registrazioni Utente Selezionato (ZIP)
                                </button>
                            </div>

                            {filteredUsers.length === 0 ? (
                                <p style={styles.message}>Nessun utente corrisponde alla ricerca.</p>
                            ) : (
                                <div>
                                    {/* Controllo navigazione utenti */}
                                    <div style={styles.navigationContainer}>
                                        <button
                                            onClick={() => setCurrentUserIndex(prev => Math.max(0, prev - 1))}
                                            disabled={currentUserIndex === 0}
                                            style={{
                                                ...styles.navButton,
                                                ...(currentUserIndex === 0 ? styles.disabledNavButton : {})
                                            }}
                                        >
                                            &larr; Precedente
                                        </button>
                                        
                                        <div style={styles.selectWrapper}>
                                            <select
                                                value={currentUserIndex}
                                                onChange={(e) => setCurrentUserIndex(Number(e.target.value))}
                                                style={styles.userSelect}
                                            >
                                                {filteredUsers.map((u, index) => (
                                                    <option key={u.id} value={index}>
                                                        Utente ID: {u.id} ({index + 1} di {filteredUsers.length})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <button
                                            onClick={() => setCurrentUserIndex(prev => Math.min(filteredUsers.length - 1, prev + 1))}
                                            disabled={currentUserIndex === filteredUsers.length - 1}
                                            style={{
                                                ...styles.navButton,
                                                ...(currentUserIndex === filteredUsers.length - 1 ? styles.disabledNavButton : {})
                                            }}
                                        >
                                            Successivo &rarr;
                                        </button>
                                    </div>

                                    {/* Mostra solo la card dell'utente attivo */}
                                    {filteredUsers[currentUserIndex] && (
                                        <div key={filteredUsers[currentUserIndex].id} style={styles.userCard}>
                                            <h2 style={styles.userTitle}>Utente ID: {filteredUsers[currentUserIndex].id}</h2>
                                            {filteredUsers[currentUserIndex].activities.map(activity => (
                                                <div key={activity.id} style={styles.activityBox}>
                                                    <h3 style={styles.activityTitle}>{activity.name}</h3>
                                                    {activity.levels.map(level => (
                                                        <div key={level.id} style={styles.levelBox}>
                                                            {level.exercises.map(exercise => (
                                                                <div key={exercise.id} style={styles.exerciseRow}>
                                                                    <span style={styles.exerciseText}>
                                                                        Livello {level.levelNumber} - Es. {exercise.exerciseNumber}
                                                                    </span>
                                                                    <div style={styles.actions}>
                                                                        <audio
                                                                            src={getAssetUrl(exercise.recordingPath, dbType)}
                                                                            controls
                                                                            style={styles.audioPlayer}
                                                                        />
                                                                        <button
                                                                            onClick={() => downloadRecording(exercise.id, filteredUsers[currentUserIndex].id, activity.name, level.levelNumber, exercise.exerciseNumber)}
                                                                            style={styles.downloadBtn}
                                                                        >
                                                                            Scarica
                                                                        </button>
                                                                        <button
                                                                            onClick={() => deleteRecording(exercise.id)}
                                                                            style={styles.deleteBtn}
                                                                        >
                                                                            Elimina
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    container: {
        position: 'relative',
        zIndex: 2,
        maxWidth: '1000px',
        margin: '0 auto',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        padding: '30px',
        borderRadius: '15px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
        fontFamily: "'Autour One', cursive",
    },
    navigationContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '25px',
        padding: '10px 15px',
        backgroundColor: '#f0f4f8',
        borderRadius: '10px',
        border: '1px solid #d9e2ec',
        gap: '15px',
        flexWrap: 'wrap',
    },
    navButton: {
        fontFamily: "'Autour One', cursive",
        backgroundColor: '#64798A',
        color: '#fff',
        border: 'none',
        padding: '10px 20px',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '15px',
        transition: 'background-color 0.2s ease, transform 0.1s ease',
        boxShadow: '0 4px 6px rgba(50, 50, 93, 0.11), 0 1px 3px rgba(0, 0, 0, 0.08)',
    },
    disabledNavButton: {
        backgroundColor: '#bcccdc',
        color: '#f0f4f8',
        cursor: 'not-allowed',
        boxShadow: 'none',
    },
    selectWrapper: {
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        minWidth: '200px',
    },
    userSelect: {
        fontFamily: "'Autour One', cursive",
        fontSize: '16px',
        padding: '8px 15px',
        borderRadius: '8px',
        border: '2px solid #64798A',
        color: '#334e68',
        backgroundColor: '#fff',
        cursor: 'pointer',
        outline: 'none',
        maxWidth: '300px',
        width: '100%',
        boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.075)',
    },
    searchContainer: {
        marginBottom: '20px',
        display: 'flex',
        justifyContent: 'center',
    },
    searchInput: {
        fontFamily: "'Autour One', cursive",
        fontSize: '16px',
        padding: '10px 20px',
        borderRadius: '25px',
        border: '2px solid #64798A',
        outline: 'none',
        width: '100%',
        maxWidth: '400px',
        color: '#334e68',
        backgroundColor: '#fff',
        boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
        transition: 'border-color 0.2s, box-shadow 0.2s',
    },
    title: {
        textAlign: 'center',
        color: '#333',
        marginBottom: '30px',
    },
    message: { textAlign: 'center', fontSize: '20px' },
    error: { textAlign: 'center', color: 'red', fontSize: '18px' },
    userList: { display: 'flex', flexDirection: 'column', gap: '20px' },
    userCard: {
        border: '2px solid #64798A',
        borderRadius: '10px',
        padding: '15px',
        backgroundColor: '#f9f9f9',
    },
    userTitle: { color: '#2c3e50', borderBottom: '2px solid #bdc3c7', paddingBottom: '10px' },
    activityBox: { marginTop: '15px', paddingLeft: '15px' },
    activityTitle: { color: '#2980b9', textTransform: 'capitalize' },
    levelBox: { marginLeft: '15px' },
    exerciseRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px',
        borderBottom: '1px solid #ddd',
        flexWrap: 'wrap',
        gap: '10px'
    },
    exerciseText: { fontSize: '16px', color: '#555' },
    actions: { display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' },
    audioPlayer: { height: '35px' },
    downloadBtn: {
        backgroundColor: '#27ae60',
        color: 'white',
        border: 'none',
        padding: '8px 15px',
        borderRadius: '5px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 'bold',
    },
    deleteBtn: {
        backgroundColor: '#e74c3c',
        color: 'white',
        border: 'none',
        padding: '8px 15px',
        borderRadius: '5px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 'bold',
    },
    logButtonsContainer: {
        display: 'flex',
        justifyContent: 'center',
        gap: '15px',
        marginBottom: '20px',
        flexWrap: 'wrap',
    },
    logBtn: {
        fontFamily: "'Autour One', cursive",
        backgroundColor: '#34495e',
        color: 'white',
        border: 'none',
        padding: '10px 20px',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '14px',
        transition: 'background-color 0.2s, transform 0.1s',
        boxShadow: '0 4px 6px rgba(50, 50, 93, 0.11), 0 1px 3px rgba(0, 0, 0, 0.08)',
    },
    logBtnSelected: {
        fontFamily: "'Autour One', cursive",
        backgroundColor: '#2980b9',
        color: 'white',
        border: 'none',
        padding: '10px 20px',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '14px',
        transition: 'background-color 0.2s, transform 0.1s',
        boxShadow: '0 4px 6px rgba(50, 50, 93, 0.11), 0 1px 3px rgba(0, 0, 0, 0.08)',
    }
};
